import React from "react";
import {
  WSActions,
  WSButton,
  WSCard,
  WSControl,
  WSFlexBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { WSQueries } from "../../../../query/WSQuery";
import { OnboardingStep } from "../../../Onboarding/types";
import { useQueryMeridioPlanCatalog } from "../../../../query/benefits/useQueryMeridioPlanCatalog";
import { useFlowContext } from "../../../../shared/components/Flow";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import {
  IMeridioPlanCatalogCategory,
  IMeridioPlanCatalogResponse,
  IMeridioPlanOption
} from "../../../../services/api/benefits/meridioPlanCatalog/types";
import { CoverageTier, EnrollMeridioFlowState } from "./types";

const TIER_CONFIG: Array<{ tier: CoverageTier; label: string }> = [
  { tier: "IO", label: "Individual (just you)" },
  { tier: "IS", label: "Individual + Spouse (you and your spouse)" },
  { tier: "IC", label: "Individual + Dependents (you and your child(ren))" },
  { tier: "IF", label: "Family (you, spouse, and children)" }
];

const TIER_KEY_MAP: Record<CoverageTier, "io" | "is" | "ic" | "if"> = {
  IO: "io",
  IS: "is",
  IC: "ic",
  IF: "if"
};

function findPlan(
  categories: IMeridioPlanCatalogCategory[],
  baseSku: string
): IMeridioPlanOption | undefined {
  for (const cat of categories) {
    const found = (cat.plans as IMeridioPlanOption[]).find(
      p => p.baseSku === baseSku
    );
    if (found) return found;
  }
  return undefined;
}

export const StepSelectCoverage: React.FC<OnboardingStep> = props => {
  const queryMeridioPlanCatalog = useQueryMeridioPlanCatalog();
  const [flowState, setFlowState] = useFlowContext<EnrollMeridioFlowState>();

  const allSelectedSkus = [
    flowState.healthPlanSku,
    ...(flowState.voluntaryPlanSkus ?? []),
    flowState.dentalVisionPlanSku
  ].filter((s): s is string => !!s);

  const hasAllTiersSelected =
    allSelectedSkus.length > 0 &&
    allSelectedSkus.every(sku => flowState.planSelections?.[sku]);

  const handleRemove = (baseSku: string) => {
    setFlowState(prev => {
      const next = { ...prev };
      if (next.healthPlanSku === baseSku) next.healthPlanSku = undefined;
      if (next.dentalVisionPlanSku === baseSku)
        next.dentalVisionPlanSku = undefined;
      if (next.voluntaryPlanSkus) {
        next.voluntaryPlanSkus = next.voluntaryPlanSkus.filter(
          s => s !== baseSku
        );
      }
      const nextSelections = { ...(next.planSelections ?? {}) };
      delete nextSelections[baseSku];
      next.planSelections = nextSelections;
      return next;
    });
  };

  const handleTierSelect = (baseSku: string, tier: CoverageTier) => {
    setFlowState(prev => ({
      ...prev,
      planSelections: { ...(prev.planSelections ?? {}), [baseSku]: tier }
    }));
  };

  return (
    <WSQueries queries={{ queryMeridioPlanCatalog }}>
      {({
        queryMeridioPlanCatalogData
      }: {
        queryMeridioPlanCatalogData: IMeridioPlanCatalogResponse;
      }) => (
        <>
          <WSText.Heading3 weight="medium" mb="S">
            Select coverage
          </WSText.Heading3>
          <WSText.ParagraphSm weight="book" color="gray500" mb="XL">
            Select who is covered under for each selected plan. Cost will update
            based on who is covered. To change or add plans, click
            &quot;Back&quot; to the previous page.
          </WSText.ParagraphSm>
          <WSList gap="XL">
            {allSelectedSkus.map(baseSku => {
              const plan = findPlan(
                queryMeridioPlanCatalogData.categories,
                baseSku
              );
              if (!plan) return null;
              const selectedTier = flowState.planSelections?.[baseSku];
              return (
                <WSCard key={baseSku}>
                  <WSFlexBox.CenterY justify="space-between" mb="M">
                    <WSText.Heading5 weight="medium">
                      {plan.name}
                    </WSText.Heading5>
                    <WSButton
                      kind="Secondary"
                      destructive
                      onClick={() => handleRemove(baseSku)}
                    >
                      Remove
                    </WSButton>
                  </WSFlexBox.CenterY>
                  <WSText.ParagraphSm weight="book" color="gray500" mb="L">
                    Who needs coverage? *
                  </WSText.ParagraphSm>
                  <WSList gap="L">
                    {TIER_CONFIG.map(({ tier, label }) => {
                      const tierKey = TIER_KEY_MAP[tier];
                      const pricing = plan[tierKey];
                      if (!pricing) return null;
                      return (
                        <WSFlexBox.CenterY key={tier} justify="space-between">
                          <WSControl
                            type="radio"
                            value={selectedTier === tier}
                            onChange={() => handleTierSelect(baseSku, tier)}
                            label={label}
                          />
                          <WSText.ParagraphSm weight="book" color="gray500">
                            {formatMoney(pricing.monthlyPremium, Currency.USD)}
                            /month
                          </WSText.ParagraphSm>
                        </WSFlexBox.CenterY>
                      );
                    })}
                  </WSList>
                </WSCard>
              );
            })}
          </WSList>
          <WSActions
            mt="XL"
            alignment="fill"
            buttons={[
              { kind: "Tertiary", label: "Back", onClick: props.onBack },
              {
                label: "Continue",
                onClick: props.onNext,
                disabled: !hasAllTiersSelected
              }
            ]}
          />
        </>
      )}
    </WSQueries>
  );
};
